import { useEffect, useState } from 'react';

/**
 * Shares the `?detail=<itemId>` URL convention (introduced by
 * `JiraDetailModal`) across every provider's detail surface so browser Back
 * closes the shell before leaving the provider workspace, and a reload keeps
 * the detail open for the same item.
 *
 * One query param is shared across providers because only one detail
 * surface can be open at a time (design.md — context and detail sheets are
 * mutually exclusive).
 */
export function useDetailSurfaceParam(itemId: string): [boolean, (open: boolean) => void] {
  const [isOpen, setIsOpenState] = useState(() => {
    if (typeof window === 'undefined') return false;
    return new URLSearchParams(window.location.search).get('detail') === itemId;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const url = new URL(window.location.href);
    if (isOpen) url.searchParams.set('detail', itemId);
    else if (url.searchParams.get('detail') === itemId) url.searchParams.delete('detail');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [isOpen, itemId]);

  useEffect(() => {
    const onPopState = () => {
      setIsOpenState(new URLSearchParams(window.location.search).get('detail') === itemId);
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [itemId]);

  const setIsOpen = (open: boolean) => setIsOpenState(open);

  return [isOpen, setIsOpen];
}
